<template>
  <footer class="site-footer" :style="footerBgStyle">
    <div class="footer-inner">
      <div class="footer-main" :class="{ 'has-friends': cfg.friendLinks.length }">
        <section class="footer-col footer-contact">
          <div class="footer-col-body">
            <p v-if="cfg.address" class="footer-line">
              <span class="footer-ico" v-html="icons.location" aria-hidden="true"></span>
              <span>{{ cfg.address }}</span>
            </p>
            <p v-if="cfg.postcode" class="footer-line">
              <span class="footer-ico" v-html="icons.mail" aria-hidden="true"></span>
              <span>邮编: {{ cfg.postcode }}</span>
            </p>
            <p v-if="cfg.contactPhone" class="footer-line">
              <span class="footer-ico" v-html="icons.phone" aria-hidden="true"></span>
              <span>电话: {{ cfg.contactPhone }}</span>
            </p>
            <p v-if="cfg.fax" class="footer-line">
              <span class="footer-ico" v-html="icons.fax" aria-hidden="true"></span>
              <span>传真: {{ cfg.fax }}</span>
            </p>
            <p v-if="cfg.contactEmail" class="footer-line">
              <span class="footer-ico" v-html="icons.mail" aria-hidden="true"></span>
              <span>邮箱: {{ cfg.contactEmail }}</span>
            </p>
          </div>
        </section>

        <section v-if="cfg.friendLinks.length" class="footer-col footer-friends">
          <h3>友情链接</h3>
          <div class="footer-col-body">
            <ul>
              <li v-for="link in cfg.friendLinks" :key="link.name + link.url">
                <a :href="link.url" target="_blank" rel="noopener">{{ link.name }}</a>
              </li>
            </ul>
          </div>
        </section>

        <section class="footer-col footer-follow">
          <h3>关注我们</h3>
          <div class="footer-col-body">
            <div class="footer-qr-row">
              <div class="footer-qr">
                <div class="footer-qr-box">
                  <img v-if="cfg.weiboQrcode" :src="resolveUrl(cfg.weiboQrcode)" alt="官方微博" />
                  <span v-else class="footer-qr-placeholder">QR</span>
                </div>
                <span>官方微博</span>
              </div>
              <div class="footer-qr">
                <div class="footer-qr-box">
                  <img v-if="cfg.wechatQrcode" :src="resolveUrl(cfg.wechatQrcode)" alt="公众号" />
                  <span v-else class="footer-qr-placeholder">QR</span>
                </div>
                <span>公众号</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="footer-bottom">
        <span>{{ cfg.copyright }}</span>
        <div class="footer-filings">
          <span v-if="cfg.icp">{{ cfg.icp }}</span>
          <span v-if="cfg.icp && cfg.policeRecord" class="footer-sep">|</span>
          <span v-if="cfg.policeRecord">{{ cfg.policeRecord }}</span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import deepSeaBg from '../../assets/footer-deep-sea.png'

/** 统一金色描线图标，避免系统 emoji（尤其传真 📠）颜色不一致 */
const stroke = 'currentColor'
function svg(paths) {
  return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">${paths}</svg>`
}

export default {
  name: 'SiteFooter',
  inject: ['front'],
  data() {
    return {
      deepSeaBg,
      icons: {
        location: svg(
          `<path d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11z" stroke="${stroke}" stroke-width="1.6" stroke-linejoin="round"/>` +
          `<circle cx="12" cy="10" r="2.2" stroke="${stroke}" stroke-width="1.6"/>`
        ),
        mail: svg(
          `<rect x="3.5" y="5.5" width="17" height="13" rx="1.5" stroke="${stroke}" stroke-width="1.6"/>` +
          `<path d="M4.5 7.5 12 12.5 19.5 7.5" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`
        ),
        phone: svg(
          `<path d="M7.2 3.8h2.6l1.2 3.2-1.6 1.2a12.5 12.5 0 0 0 5.4 5.4l1.2-1.6 3.2 1.2v2.6c0 .9-.7 1.6-1.6 1.6A14.8 14.8 0 0 1 3.8 5.4c0-.9.7-1.6 1.6-1.6z" stroke="${stroke}" stroke-width="1.6" stroke-linejoin="round"/>`
        ),
        // 传真：简洁文档/传真机线框，与图二金色线标风格一致
        fax: svg(
          `<rect x="5" y="3.5" width="14" height="8" rx="1.2" stroke="${stroke}" stroke-width="1.6"/>` +
          `<path d="M7 14.5h10" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round"/>` +
          `<path d="M8.5 17h7" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round"/>` +
          `<path d="M10 19.5h4" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round"/>` +
          `<path d="M8 7.5h8" stroke="${stroke}" stroke-width="1.4" stroke-linecap="round"/>`
        )
      }
    }
  },
  computed: {
    cfg() {
      const c = this.front.siteConfig || {}
      return {
        copyright: c.copyright || '© 2026 中国古生物学会 版权所有',
        address: c.address || '',
        postcode: c.postcode || '',
        contactPhone: c.contactPhone || '',
        fax: c.fax || '',
        contactEmail: c.contactEmail || '',
        icp: c.icp || '',
        policeRecord: c.policeRecord || '',
        weiboQrcode: c.weiboQrcode || c.memberQrcode || '',
        wechatQrcode: c.wechatQrcode || '',
        friendLinks: Array.isArray(c.friendLinks) ? c.friendLinks : []
      }
    },
    footerBgStyle() {
      return {
        backgroundColor: '#1D3173',
        backgroundImage: `linear-gradient(180deg, rgba(29, 49, 115, 0.78), rgba(29, 49, 115, 0.72)), url(${this.deepSeaBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 36%',
        backgroundRepeat: 'no-repeat'
      }
    }
  },
  methods: {
    resolveUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url
      return `${process.env.VUE_APP_BASE_API || ''}${url}`
    }
  }
}
</script>
